/**
 * Live master-salary calculation — mirrors backend/helpers/salary_master.py exactly.
 * Used by the editable Employee Master grid so the figures update as HR types.
 */
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const roundHalf = (a) => Math.floor(a + 0.5);
const r2 = (a) => Math.round(a * 100) / 100;

export const computePfSlab = (basicPlusDa) =>
  basicPlusDa <= 0 ? 0 : roundHalf(Math.min(basicPlusDa, 15000) * 0.12);

export const computeProfessionTax = (gross, gender, month) => {
  const isFeb = !!(month && String(month).endsWith('-02'));
  const g = (gender || 'male').trim().toLowerCase();
  if (g === 'female') return gross <= 25000 ? 0 : (isFeb ? 300 : 200);
  if (gross > 10000) return isFeb ? 300 : 200;
  if (gross >= 7501 && gross <= 10000) return 175;
  return 0;
};

export function computeMaster(emp, month, claCfg) {
  const consolidated = (emp.salary_type || 'breakdown').toLowerCase() === 'consolidated';
  let grossBasic = 0, da = 0, hra = 0, cla = 0, gross = 0, pf = 0, pfEmployer = 0;

  if (consolidated) {
    gross = num(emp.consolidated_salary);
  } else {
    grossBasic = num(emp.basic_salary) + num(emp.agp);
    const pct = num(emp.da_percentage);
    da = pct > 0 ? r2(grossBasic * pct / 100) : num(emp.da);
    hra = pct > 0 ? (num(emp.hra) > 0 ? num(emp.hra) : r2(grossBasic * 0.10)) : num(emp.hra);
    const isNon = (emp.category || '').toLowerCase().includes('non');
    if (claCfg) {
      cla = claCfg.enabled === false ? 0 : num(isNon ? claCfg.non_teaching : claCfg.teaching);
    } else {
      cla = isNon ? 400 : 800;
    }
    let special = num(emp.special_allowance);
    let other = num(emp.other_allowance);
    if (special <= 0 && other > 0) { special = other; other = 0; }
    gross = r2(grossBasic + da + hra + cla + num(emp.hod_allowance) + special + other);
    const pfStored = num(emp.provident_fund);
    if (pfStored > 0) pf = pfStored;
    else if (emp.pf_applicable !== false) pf = computePfSlab(grossBasic + da);
    pfEmployer = pf > 0 ? roundHalf(Math.min(grossBasic + da, 15000) * 0.13) : 0;
  }

  const ptStored = num(emp.profession_tax);
  let pt = 0;
  if (emp.is_handicapped === true || emp.is_senior_citizen === true) pt = 0;
  else if (ptStored > 0) pt = ptStored;
  else if (emp.pt_applicable !== false) pt = computeProfessionTax(gross, emp.gender, month);

  const esicStored = num(emp.esic);
  let esic = 0, esicEmployer = 0;
  if (esicStored > 0) {
    esic = esicStored;
    esicEmployer = Math.ceil(gross * 0.0325);
  } else if (emp.esic_applicable === true && gross > 0 && gross <= 21000) {
    esic = Math.ceil(gross * 0.0075);
    esicEmployer = Math.ceil(gross * 0.0325);
  }

  const fixed = num(emp.tds) + num(emp.bus) + num(emp.accommodation)
    + num(emp.localmart) + num(emp.other_deduction);
  const totalDeductions = r2(pf + esic + pt + fixed);

  return {
    gross_basic: r2(grossBasic),
    da_amount: r2(da),
    hra_amount: r2(hra),
    cla_amount: r2(cla),
    gross_salary: r2(gross),
    pf_amount: r2(pf),
    pf_employer: r2(pfEmployer),
    profession_tax_amount: r2(pt),
    esic_amount: r2(esic),
    esic_employer: r2(esicEmployer),
    other_deductions_fixed: r2(fixed),
    total_deductions: totalDeductions,
    net_salary: r2(gross - totalDeductions),
  };
}

export default computeMaster;
